import React from "react";
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import Link from '@mui/joy/Link';

/**
 * ReferencesCard - Displays Token Usage, Origin Task Reference, References, and Policy Output
 */
const ReferencesCard = ({
  id,
  tokenUsage,
  references,
  originalTaskId,
  policyOutputId,
}) => {
  const hasTokenUsage = tokenUsage && references && Object.keys(references).length > 0;
  const hasReferences = references && Object.keys(references).length > 0;
  const hasOriginalTask = !!originalTaskId;
  const hasPolicyOutput = !!policyOutputId;

  // If nothing to show, return null
  if (!hasTokenUsage && !hasReferences && !hasOriginalTask && !hasPolicyOutput) {
    return null;
  }

  return (
    <>
      {/* Token Usage */}
      {hasTokenUsage && (
        <Card variant="outlined" sx={{ px: 3 }}>
          <CardContent>
            <Typography level="h3">Token Usage</Typography>
            <Typography level="body-md" mb={2}>
              Here's a breakdown of the tokens used throughout the task no. {id}
            </Typography>

            <Grid container direction="column" spacing={1}>
              <Grid item container alignItems="center" spacing={1}>
                <Grid item>
                  <Typography level="title-md">Input Tokens Used:</Typography>
                </Grid>
                <Grid item>
                  <Chip>{tokenUsage.input_tokens}</Chip>
                </Grid>
              </Grid>

              <Grid item container alignItems="center" spacing={1}>
                <Grid item>
                  <Typography level="title-md">Output Tokens Used:</Typography>
                </Grid>
                <Grid item>
                  <Chip>{tokenUsage.output_tokens}</Chip>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Origin Task Reference */}
      {hasOriginalTask && (
        <Card variant="outlined" color="primary" invertedColors sx={{ px: 3, mt: 2, borderRadius: 'lg' }}>
          <CardContent>
            <Typography level="h3">
              Origin Task Reference
            </Typography>

            <Typography level="body-md" mt={1} mb={2}>
              This result was generated from the following task:
            </Typography>

            <Chip
              component={Link}
              href={`/app/tasks/result/${originalTaskId}`}
              variant="soft"
              color="primary"
              sx={{
                textDecoration: 'none',
                fontWeight: 'md',
                fontSize: 'md',
                px: 2,
                py: 1,
                borderRadius: 'md',
              }}
            >
              View Task #{originalTaskId}
            </Chip>
          </CardContent>
        </Card>
      )}

      {/* References */}
      {hasReferences && (
        <Card variant="outlined" sx={{ px: 3 }}>
          <CardContent>
            <Typography level="h3">References</Typography>
            <Typography level="body-md" mb={2}>Here are the references for task no. {id}</Typography>
            {Object.entries(references).map(([key, value]) => (
              <Stack key={key} direction="column" spacing={1}>
                <Typography level="title-md">{key}:</Typography>
                <Typography level="body-sm" mb={2}>{value}</Typography>
              </Stack>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Policy Output */}
      {hasPolicyOutput && (
        <Card variant="outlined" sx={{ px: 3 }}>
          <CardContent>
            <Typography level="h3">Policy Output</Typography>
            <Typography level="body-md" mb={2}>
              Associated Policy Output ID for task no. {id}
            </Typography>
            <Stack direction="row" justifyContent="space-between">
              <Typography level="title-md">Policy Output ID:</Typography>
              <Chip>{policyOutputId}</Chip>
            </Stack>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ReferencesCard;

